/**
 * Application constants
 */

/**
 * Toggle API endpoint via environment variable:
 * REACT_APP_USE_LOCAL_API=true  -> use local development server
 * REACT_APP_USE_LOCAL_API=false -> use production API server (default)
 */
const useLocalFromEnv =
  (process.env.REACT_APP_USE_LOCAL_API ?? "").toLowerCase() === "true";
export const USE_LOCAL_API = useLocalFromEnv;

/**
 * API endpoint configurations
 */
export const API_ENDPOINTS = {
  // Base URLs
  LOCAL_BASE_URL: "http://localhost:8080",
  DEPLOYED_BASE_URL: `https://api.qazbilim.com`,

  // Get base URL based on current environment configuration
  get BASE_URL() {
    return USE_LOCAL_API ? this.LOCAL_BASE_URL : this.DEPLOYED_BASE_URL;
  },

  // API paths
  AUTHORS: "/authors",
  BOOKS: "/books",
  UPLOAD_DOC: "/files/",
  USERS: "/users",
  USER_LOGIN: "/users/login",
  CONVERT: "/convert",
  CONVERT_SUPPORTED_TYPES: "/convert/supported-types",
  SPLIT_CHAPTERS: "/split-chapters",
  TRANSLATE: "/translate",
  TRANSLATE_LANGUAGES: "/translate/languages",

  // Full API URLs
  get AUTHORS_URL() {
    return `${this.BASE_URL}${this.AUTHORS}`;
  },
  get BOOKS_URL() {
    return `${this.BASE_URL}${this.BOOKS}`;
  },
  get UPLOAD_URL() {
    return `${this.BASE_URL}${this.UPLOAD_DOC}`;
  },
  get USERS_URL() {
    return `${this.BASE_URL}${this.USERS}`;
  },
  get USER_LOGIN_URL() {
    return `${this.BASE_URL}${this.USER_LOGIN}`;
  },
  get CONVERT_URL() {
    return `${this.BASE_URL}${this.CONVERT}`;
  },
  get CONVERT_SUPPORTED_TYPES_URL() {
    return `${this.BASE_URL}${this.CONVERT_SUPPORTED_TYPES}`;
  },
  get SPLIT_CHAPTERS_URL() {
    return `${this.BASE_URL}${this.SPLIT_CHAPTERS}`;
  },
  get TRANSLATE_URL() {
    return `${this.BASE_URL}${this.TRANSLATE}`;
  },
  get TRANSLATE_LANGUAGES_URL() {
    return `${this.BASE_URL}${this.TRANSLATE_LANGUAGES}`;
  },
};

/**
 * Public site origin for share/copy links. Always production — never localhost —
 * so social networks and messengers get a stable, scrapable URL.
 */
export const PUBLIC_SITE_ORIGIN = "https://www.qazbilim.com";

/**
 * Absolute URL to the SPA reader route (`/read/:bookId/:chapter`), for social share dialogs.
 * Built from {@link PUBLIC_SITE_ORIGIN} plus `PUBLIC_URL` path prefix if the app is hosted in a subpath.
 */
export function buildReaderBookShareUrl(
  bookId: string,
  chapterOneBased: number,
): string {
  const pub = (process.env.PUBLIC_URL ?? "").replace(/\/$/, "");
  const chapter = Math.max(1, Math.floor(chapterOneBased));
  const path = `${pub}/read/${encodeURIComponent(bookId)}/${chapter}`;
  return `${PUBLIC_SITE_ORIGIN.replace(/\/$/, "")}${path}`;
}

/**
 * Optional override for the **origin** used in {@link buildShareBookOgPageUrl} only.
 * Omit to use production defaults (`www` pretty path, or API path when using a local API).
 */
function shareBookOgOriginOverride(): string | null {
  const fromEnv = (process.env.REACT_APP_SHARE_BOOK_BASE_URL ?? "")
    .trim()
    .replace(/\/$/, "");
  return fromEnv || null;
}

/**
 * URL crawlers should fetch for rich previews (`og:image`, title, description).
 *
 * Production uses `{@link PUBLIC_SITE_ORIGIN}/share/read/{bookId}/{chapter}` so the link stays on
 * `www`. Vercel must rewrite that path to the API (`/share/book/...`); see `vercel.json`.
 *
 * The in-app reader remains `{@link PUBLIC_SITE_ORIGIN}/read/{bookId}/{chapter}`.
 *
 * With `REACT_APP_USE_LOCAL_API`, this points at {@link API_ENDPOINTS.BASE_URL}`/share/book/...`
 * (no Vercel proxy). With `REACT_APP_SHARE_BOOK_BASE_URL`, that origin is used with `/share/book/...`.
 */
export function buildShareBookOgPageUrl(
  bookId: string,
  chapterOneBased: number,
): string {
  const chapter = Math.max(1, Math.floor(chapterOneBased));
  const id = encodeURIComponent(bookId);
  const baseOverride = shareBookOgOriginOverride();
  if (baseOverride) {
    return `${baseOverride}/share/book/${id}/${chapter}`;
  }
  if (USE_LOCAL_API) {
    return `${API_ENDPOINTS.BASE_URL.replace(/\/$/, "")}/share/book/${id}/${chapter}`;
  }
  return `${PUBLIC_SITE_ORIGIN.replace(/\/$/, "")}/share/read/${id}/${chapter}`;
}

/**
 * Other application constants
 */
export const APP_CONSTANTS = {
  // Import fallback image from public folder
  BOOK_COVER_PLACEHOLDER: `${process.env.PUBLIC_URL}/data/fall_back.jpg`,
  DEFAULT_TOAST_DURATION: 3000,
};
